/**
 * Anket metinlerinin uygulamanın seçili diline bağlanması (096 / FR-013, FR-016).
 *
 * THY'nin hazır bileşeni metinleri Türkçe sabit getiriyor. TK Seyir'in eksiksiz bir İngilizce
 * arayüzü olduğu için bağlanmazsa İngilizce çalışan kullanıcı karışık dilli bir ekran görür.
 *
 * React ağacı dışından çağrılır (tetikleme bir `MutationCache` geri çağrısıdır, bileşen değil);
 * bu yüzden `useTranslation()` yerine `i18n` doğrudan kullanılır — depoda emsali var
 * (`api/client.ts`, `api/errorNotifier.ts`).
 */

import i18n from '../../i18n'
import type { AppFeedbackConfig, AppFeedbackValueMap } from './widgetTypes'

/** Bileşenin beklediği anahtar biçimi `value_1..value_5`; bizim i18n anahtarlarımız `value1..value5`. */
const SCORES = [1, 2, 3, 4, 5] as const

/** Anket metinleri — taban adres hariç (o ortam yapılandırmasından gelir). */
export type AppFeedbackTexts = Omit<AppFeedbackConfig, 'apiUrl'>

/**
 * Çeviriden bir dize dizisi okur.
 *
 * `returnObjects` ile gelen değer `unknown`'dır; `any`'ye düşmeden (anayasa İlke IV) daraltılır.
 * Anahtar çözülemezse i18next anahtarın kendisini döndürür — dizi beklendiği yerde bu bir dize
 * olur ve boş listeye düşeriz. Sessiz bir bozulma yerine boş liste doğru davranıştır: hazır
 * yorumlar isteğe bağlı bir kolaylıktır, yokluğu anketi çalışmaz hâle getirmez.
 */
function readStringList(key: string): string[] {
  const value: unknown = i18n.t(key, { returnObjects: true })
  if (!Array.isArray(value)) return []
  return value.filter((item): item is string => typeof item === 'string')
}

function buildValueMap<T>(read: (score: number) => T): AppFeedbackValueMap<T> {
  const [one, two, three, four, five] = SCORES.map(read)
  return { value_1: one, value_2: two, value_3: three, value_4: four, value_5: five }
}

/**
 * Bileşene atanacak metinleri seçili dilde üretir.
 *
 * Her tetiklemede yeniden çağrılır — kullanıcı dili anket açılmadan önce değiştirmiş olabilir ve
 * metinler o anki dile göre üretilmelidir.
 */
export function buildAppFeedbackTexts(): AppFeedbackTexts {
  return {
    title: i18n.t('appFeedback.title'),
    description: i18n.t('appFeedback.description'),
    successText: i18n.t('appFeedback.successText'),
    errorText: i18n.t('appFeedback.errorText'),
    placeholders: buildValueMap((score) => i18n.t(`appFeedback.placeholders.value${score}`)),
    preparedComments: buildValueMap((score) =>
      readStringList(`appFeedback.preparedComments.value${score}`),
    ),
  }
}
